import { Component, Input, Output, EventEmitter } from '@angular/core';
import { EventMemberModel } from '../../models/events/event-member.model';
import { TableColumn } from '../../models/table-column.model';
import { DxTabsTypes } from 'devextreme-angular/ui/tabs';
import { TrainingService } from 'src/app/services/training.service';
import { LoadingService } from 'src/app/services/loading.service';
import { PersonService } from 'src/app/services/person.service';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { PersonCreationAndUpdateModel } from 'src/app/models/person/person-creation-and-update.model';
import { ConfirmationDialogData } from '../shared/confirmation-dialog/confirmation-dialog.component';
import { first, map, of, switchMap } from 'rxjs';
import { AuthorizationService } from 'src/app/services/authorization.service';
import { PersonModel } from 'src/app/models/person/person.model';
import { EmployeeStatusTabsModel } from 'src/app/models/employee-status.model';
import { TrainingChannelStatisticsModel } from 'src/app/models/training/training-channel-statistics.model';
import { DatePipe } from '@angular/common';
import { Router } from '@angular/router';
import { FileUtilService } from 'src/app/services/file-util.service';
import { AdditionalParticipantModel } from 'src/app/models/event-participations/additional-participant.model';

@Component({
  selector: 'app-training-employee-popup',
  templateUrl: './training-employee-popup.component.html',
  styleUrls: ['./training-employee-popup.component.scss'],
  standalone: false
})
export class TrainingEmployeePopupComponent {
  @Input()
  isPopupVisible!: boolean;

  @Input()
  tab: number = 0;

  @Input()
  acceptedMembers: EventMemberModel[] = [];

  @Input()
  openMembers: EventMemberModel[] = [];

  @Input()
  declinedMembers: EventMemberModel[] = [];

  @Input()
  requestedMembers: EventMemberModel[] = [];

  @Input()
  queueMembers: EventMemberModel[] = [];

  @Input()
  completedMembers: EventMemberModel[] = [];

  @Input()
  channels: TrainingChannelStatisticsModel[] = [];

  @Input()
  trainingId!: string;

  @Input()
  isClosedTraining!: boolean;

  @Input()
  isFullyBookedTraining: boolean = false;

  @Input()
  isFromSubscribedSource: boolean = false;

  @Output() onPopupClose = new EventEmitter();
  @Output() onReloadMembers = new EventEmitter();

  persons: PersonModel[] = [];
  selectedTab: number = 0;
  editingEmployeeKey: any = null;
  personIdToDelete: string = '';
  modalOptions: ConfirmationDialogData | null = null;
  showModal = false;
  isSuperAdmin: boolean = false;

  tabs: any[] = [];

  personsColumns: TableColumn[] = [
    {
      caption: '',
      dataField: 'this',
      cellType: 'action',
      disableFiltering: true,
      cellTemplate: 'actionsTemplate',
      exportable: false,
      actions: [
        {
          icon: 'check',
          class: 'green-icon',
          isShowAction: (person: PersonModel): boolean => {
            return (
              !!this.requestedMembers.find(
                (member) => member.person.id === person.id
              ) && this.editingEmployeeKey !== person.id
            );
          },
          onClick: (person: PersonModel): void => {
            this.loadingService
              .load(
                this.loadingService.load(
                  this.trainingService.approveMemberRequest(
                    this.trainingId,
                    person.id
                  )
                )
              )
              .subscribe(() => {
                if(this.isFullyBookedTraining) {
                  this.toastrService.info(
                    this.translateService.instant('EMPLOYEE.FORM.MOVED-PERSON-TO-QUEUE')
                  );
                }

                this.onReloadMembers.emit();
              });
          },
        },
        {
          icon: 'close',
          class: 'red-icon',
          hint: this.translateService.instant('EMPLOYEE.FORM.DELETE-MEMBER'),
          isShowAction: (person: PersonModel): boolean => {
            return (
              (!!this.requestedMembers.find(
                (member) => member.person.id === person.id
              ) &&
                this.editingEmployeeKey !== person.id) ||
              (this.selectedTab === EmployeeStatusTabsModel.Accepted &&
                this.isSuperAdmin &&
                !this.isClosedTraining) ||
              (this.selectedTab === EmployeeStatusTabsModel.Open &&
                this.isSuperAdmin &&
                !this.isClosedTraining)
            );
          },
          onClick: (person: PersonModel): void => {
            if (
              this.selectedTab === EmployeeStatusTabsModel.Accepted
              || this.selectedTab === EmployeeStatusTabsModel.Open
            ) {
              this.personIdToDelete = person.id;
              this.openDeleteTrainingMemberConfirmationModal();
            } else {
              this.loadingService
                .load(
                  this.loadingService.load(
                    this.trainingService.declineMemberRequest(
                      this.trainingId,
                      person.id
                    )
                  )
                )
                .subscribe(() => {
                  this.onReloadMembers.emit();
                });
            }
          },
        },
         {
          icon: 'check',
          class: 'green-icon',
          isShowAction: (person: PersonModel): boolean => {
            return (
              !!this.queueMembers.find(
                (member) => member.person.id === person.id
              ) && this.editingEmployeeKey !== person.id
            );
          },
          onClick: (person: PersonModel): void => {
            this.loadingService
              .load(
                this.loadingService.load(
                  this.trainingService.approveTrainingQueueRequest(
                    this.trainingId,
                    person.id
                  )
                )
              )
              .subscribe(() => {
                this.onReloadMembers.emit();
              });
          },
        },
        {
          icon: 'close',
          class: 'red-icon',
          isShowAction: (person: PersonModel): boolean => {
            return (
              (!!this.queueMembers.find(
                (member) => member.person.id === person.id
              ) &&
              this.editingEmployeeKey !== person.id)
            );
          },
          onClick: (person: PersonModel): void => {
            this.loadingService
              .load(
                this.loadingService.load(
                  this.trainingService.declineTrainingQueueRequest(
                    this.trainingId,
                    person.id
                  )
                )
              )
              .subscribe(() => {
                this.onReloadMembers.emit();
              });
          },
        },
      ],
    },
    {
      caption: this.translateService.instant('COMMON.FIELDS.REQUEST-DATE'),
      dataField: 'date',
      dataType: 'date',
      cellValue: (data: PersonModel): any => {
        const queueMember = this.queueMembers.find(
          (member) => member.person.id === data.id
        );

        if(!queueMember) return '';
        return new Date(queueMember.date);
      },
      calculateSortValue: (data: PersonModel): Date | string => {
        const queueMember = this.queueMembers.find(
          (member) => member.person.id === data.id
        );

        if(!queueMember) return '';
        return new Date(queueMember.date);
      },
      calculateDisplayValue: (data: PersonModel): string => {
        const queueMember = this.queueMembers.find(
          (member) => member.person.id === data.id
        );
        if(!queueMember) return '';

        return (
          new DatePipe(this.translateService.currentLang).transform(
            new Date(queueMember.date),
            'dd.MM.yyyy'
          ) || ''
        );
      }
    },
    {
      caption: this.translateService.instant('COMMON.FIELDS.LAST-NAME'),
      dataField: 'lastName',
      allowEditing: true,
      cellTemplate: 'nameParticipantsTemplate',
    },
    {
      caption: this.translateService.instant('COMMON.FIELDS.FIRST-NAME'),
      dataField: 'firstName',
      allowEditing: true,
    },
    {
      caption: this.translateService.instant('COMMON.FIELDS.EMAIL'),
      dataField: 'email',
      allowEditing: true,
    },
    {
      caption: this.translateService.instant('COMMON.FIELDS.EMPLOYEE-ID'),
      dataField: 'agentNumber',
      allowEditing: true,
    },
    {
      caption: this.translateService.instant('COMMON.FIELDS.LOCATION'),
      dataField: 'location.name',
    },
    {
      caption: this.translateService.instant('COMMON.FIELDS.ROLE'),
      dataField: 'role.name',
    },
    {
      caption: '',
      dataField: 'this',
      cellType: 'action',
      disableFiltering: true,
      exportable: false,
      cellTemplate: 'linkTemplate',
      actions: [
        {
          icon: 'link-tab',
          isShowAction: () => true,
          onClick: (person: PersonModel, e: any): void => {
            e.stopPropagation();
            e.preventDefault();
            e.stopImmediatePropagation();
            this.navigateToDetailsNewTab(person.id);
          },
        },
      ],
    }
  ];

  channelColumns: TableColumn[] = [
    {
      caption: this.translateService.instant('COMMON.FIELDS.CHANNELS'),
      dataField: 'channel',
      disableFiltering: true,
    },
    {
      caption: this.translateService.instant('CHANNEL-TRAINING-PARTICIPANTS.COUNT-OF-PARTICIPANTS'),
      dataField: '',
      disableFiltering: true,
      cellTemplate: 'nameParticipantsTemplate',
      calculateDisplayValue: (data: TrainingChannelStatisticsModel): any => {
        return {
          value: (data.acceptedPersonsCount + data.declinedPersonsCount).toString(),
          additionalParticipants: data.acceptedAdditionalParticipants
            .concat(data.declinedAdditionalParticipants)
        };
      },
      cellValue: (channel: TrainingChannelStatisticsModel) => {
        return channel.acceptedPersonsCount + channel.declinedPersonsCount
          + channel.acceptedAdditionalParticipants.length
          + channel.declinedAdditionalParticipants.length;
      },
      calculateSortValue: (channel: TrainingChannelStatisticsModel) => {
        return channel.acceptedPersonsCount + channel.declinedPersonsCount
          + channel.acceptedAdditionalParticipants.length
          + channel.declinedAdditionalParticipants.length;
      },
    },
    {
      caption: this.translateService.instant('COMMON.FIELDS.EMPLOYEE-STATUSES.ACCEPTED'),
      dataField: 'acceptedPersonsCount',
      disableFiltering: true,
      cellTemplate: 'nameParticipantsTemplate',
      calculateDisplayValue: (data: TrainingChannelStatisticsModel): any => {
        return {
          value: data.acceptedPersonsCount.toString(),
          additionalParticipants: data.acceptedAdditionalParticipants
        };
      },
      cellValue: (data: TrainingChannelStatisticsModel) => {
        return data.acceptedPersonsCount + data.acceptedAdditionalParticipants.length;
      },
      calculateSortValue: (data: TrainingChannelStatisticsModel) => {
        return data.acceptedPersonsCount + data.acceptedAdditionalParticipants.length;
      },
    },
    {
      caption: this.translateService.instant('COMMON.FIELDS.EMPLOYEE-STATUSES.DECLINED'),
      dataField: 'declinedPersonsCount',
      disableFiltering: true,
      cellTemplate: 'nameParticipantsTemplate',
      calculateDisplayValue: (data: TrainingChannelStatisticsModel): any => {
        return {
          value: data.declinedPersonsCount.toString(),
          additionalParticipants: data.declinedAdditionalParticipants
        };
      },
      cellValue: (data: TrainingChannelStatisticsModel) => {
        return data.declinedPersonsCount + data.declinedAdditionalParticipants.length;
      },
      calculateSortValue: (data: TrainingChannelStatisticsModel) => {
        return data.declinedPersonsCount + data.declinedAdditionalParticipants.length;
      },
    },
  ];

  EmployeeStatusTabs = EmployeeStatusTabsModel;

  constructor(
    private loadingService: LoadingService,
    private trainingService: TrainingService,
    private translateService: TranslateService,
    private personService: PersonService,
    private toastrService: ToastrService,
    private authorizationService: AuthorizationService,
    private router: Router,
    private fileUtilService: FileUtilService,
  ) {
    this.authorizationService
      .getCurrentPerson()
      .pipe(first())
      .subscribe((currentUser) => {
        if (currentUser.person.role.name === 'SuperAdmin') {
          this.isSuperAdmin = true;
        }
      });
  }

  getAdditionalPersons(
    personId: string
  ): AdditionalParticipantModel[] {
    const member = this.acceptedMembers
      .concat(this.declinedMembers)
      .concat(this.queueMembers)
      .concat(this.requestedMembers)
      .find((member) => member.person.id === personId);

    return member?.additionalParticipants ?? [];
  }

  onShow() {    
    if (this.isClosedTraining) {
      this.tabs = [
        {
          id: EmployeeStatusTabsModel.Completed,
          text: `${this.translateService.instant(
            'COMMON.FIELDS.EMPLOYEE-STATUSES.COMPLETED'
          )} (${this.completedMembers.length})`,
        }
      ];

      if (this.acceptedMembers.length > 0) {
        const allAcceptedAdditionalParticipants = this.acceptedMembers.reduce((sum, member) => 
          sum + (member.additionalParticipants?.length || 0), 0
        );

        this.tabs.push({
          id: EmployeeStatusTabsModel.NoShows,
          text: `${this.translateService.instant(
            'COMMON.FIELDS.EMPLOYEE-STATUSES.NO-SHOWS'
          )} (${this.acceptedMembers.length + allAcceptedAdditionalParticipants})`,
        });
      }
    } else {
      const allAcceptedAdditionalParticipants = this.acceptedMembers.reduce((sum, member) => 
        sum + (member.additionalParticipants?.length || 0), 0
      );
      const allDeclinedAdditionalParticipants = this.declinedMembers.reduce((sum, member) => 
        sum + (member.additionalParticipants?.length || 0), 0
      );
      const allQueueAdditionalParticipants = this.queueMembers.reduce((sum, member) => 
        sum + (member.additionalParticipants?.length || 0), 0
      );
      const allRequestedAdditionalParticipants = this.requestedMembers.reduce((sum, member) => 
        sum + (member.additionalParticipants?.length || 0), 0
      );
      const tabs: any[] = [
        {
          id: EmployeeStatusTabsModel.Accepted,
          text: `${this.translateService.instant(
            'COMMON.FIELDS.EMPLOYEE-STATUSES.ACCEPTED'
          )} (${this.acceptedMembers.length + allAcceptedAdditionalParticipants})`,
        },
        {
          id: EmployeeStatusTabsModel.Open,
          text: `${this.translateService.instant(
            'COMMON.FIELDS.EMPLOYEE-STATUSES.OPEN'
          )} (${this.openMembers.length})`,
        },
        {
          id: EmployeeStatusTabsModel.Declined,
          text: `${this.translateService.instant(
            'COMMON.FIELDS.EMPLOYEE-STATUSES.DECLINED'
          )} (${this.declinedMembers.length + allDeclinedAdditionalParticipants})`,
        },
      ];

      if (this.requestedMembers.length > 0) {
        tabs.push({
          id: EmployeeStatusTabsModel.Requested,
          text: `${this.translateService.instant(
            'COMMON.FIELDS.EMPLOYEE-STATUSES.REQUESTED'
          )} (${this.requestedMembers.length + allRequestedAdditionalParticipants})`,
        });
      }

      if (this.queueMembers.length > 0) {
        tabs.push({
          id: EmployeeStatusTabsModel.Queue,
          text: `${this.translateService.instant(
            'COMMON.FIELDS.EMPLOYEE-STATUSES.QUEUE'
          )} (${this.queueMembers.length + allQueueAdditionalParticipants})`,
        });
      }

      if (!this.isFromSubscribedSource) {
        tabs.push({
          id: EmployeeStatusTabsModel.ByChannel,
          text: `${this.translateService.instant(
            'COMMON.FIELDS.EMPLOYEE-STATUSES.BY-MARKETING-CHANNEL'
          )} (${this.acceptedMembers.length + this.declinedMembers.length + allAcceptedAdditionalParticipants + allDeclinedAdditionalParticipants})`,
        });
      }

      this.tabs = tabs;
    }

    this.selectedTab = this.tab ?? 0;

    setTimeout(() => {
      this.selectedTab = this.tab ?? 0;
    }, 0);

    this.setPersonsForTab();
  }

  onClose() {
    this.onPopupClose.emit();
  }

  onTabChange(e: DxTabsTypes.ItemClickEvent) {
    this.selectedTab = e.itemData.id;
    this.setPersonsForTab();
  }

  onRequestedMemberUpdateStart(key: any) {
    this.editingEmployeeKey = key;
  }

  onRequestedMemberUpdate(event: any) {
    const personId = event.key;

    this.loadingService.load(
      this.personService.getPerson(personId).pipe(
        switchMap(person => {
          const baseCommand: PersonCreationAndUpdateModel = {
            firstName: event.data.firstName,
            lastName: event.data.lastName,
            email: event.data.email,
            agentNumber: event.data.agentNumber,
            locationId: event.data.location ? event.data.location.id : null,
            roleId: event.data.role.id,
            trainingGroupIds: person.trainingGroups.map(tg => tg.id),
            learningPathIds: person.learningPaths.map(lp => lp.id),
            culture: event.data.culture,
            timeZone: event.data.timeZone,
            city: event.data.city,
            street: event.data.street,
            zipCode: event.data.zipCode,
            iddNumber: event.data.iddNumber,
            imageBase64: null
          };

          if (!person.person.imageFile?.fileUrl) {
            return of(baseCommand);
          }

          return this.fileUtilService.avatarUrlToBase64(person.person.imageFile.fileUrl).pipe(
            map(base64 => ({
              ...baseCommand,
              imageBase64: base64
            }))
          );
        }),
        switchMap(updatedPersonCommand =>
          this.personService.updatePerson(personId, updatedPersonCommand)
        )
      )
    ).subscribe({
      next: () => {
        this.toastrService.success(
          this.translateService.instant('EMPLOYEE.FORM.EMPLOYEE-UPDATED')
        );
      },
      error: () => {
        this.translateService
          .get('COMMON.AN-ERROR-HAS-OCCURRED')
          .subscribe(value => {
            this.toastrService.error(value);
          });
      }
    });
  }

  setPersonsForTab() {
    if (this.isClosedTraining) {
      switch (this.selectedTab) {
        case EmployeeStatusTabsModel.Completed:
          this.persons = this.completedMembers.map((member) => member.person);
          break;
        case EmployeeStatusTabsModel.NoShows:
          this.persons = this.acceptedMembers.map((member) => member.person);
          break;
      }
    } else {
      switch (this.selectedTab) {
        case EmployeeStatusTabsModel.Accepted:
          this.persons = this.acceptedMembers.map((member) => member.person);
          break;
        case EmployeeStatusTabsModel.Open:
          this.persons = this.openMembers.map((member) => member.person);
          break;
        case EmployeeStatusTabsModel.Declined:
          this.persons = this.declinedMembers.map((member) => member.person);
          break;
        case EmployeeStatusTabsModel.Requested:
          this.persons = this.requestedMembers.map((member) => member.person);
          break;
        case EmployeeStatusTabsModel.Queue:
          this.persons = this.queueMembers.map((member) => member.person);
          break;
      }
    }

    this.personsColumns = this.personsColumns.map(col => {
      if (col.dataField === 'date') {
        return { 
          ...col, 
          visible: this.selectedTab === EmployeeStatusTabsModel.Queue 
        };
      }
      return col;
    });
  }

  public reloadPersonsCount() {
    if(this.selectedTab === EmployeeStatusTabsModel.Requested && this.requestedMembers.length == 0) {
      if(this.isFullyBookedTraining && this.queueMembers.length > 0) {
        this.tab = EmployeeStatusTabsModel.Queue;
      } else {
        this.tab = EmployeeStatusTabsModel.Accepted;
      }
    } else if(this.selectedTab === EmployeeStatusTabsModel.Queue && this.queueMembers.length == 0) {
      this.tab = EmployeeStatusTabsModel.Accepted;
    }

    this.onShow();
  }

  openDeleteTrainingMemberConfirmationModal() {
    this.modalOptions = {
      submitButtonText: this.translateService.instant('COMMON.DELETE'),
      message: this.translateService.instant(
        'TRAINING.DELETE-PARTICIPANT-DIALOG.TEXT'
      ),
      title: this.translateService.instant(
        'TRAINING.DELETE-PARTICIPANT-DIALOG.TITLE'
      ),
      dismissButtonText: this.translateService.instant('COMMON.NO'),
      hideUndo: true,
    };
    this.showModal = true;
  }

  deleteTrainingMember() {
    this.loadingService
      .load(
        this.loadingService.load(
          this.trainingService.deleteTrainingMember(
            this.trainingId,
            this.personIdToDelete
          )
        )
      )
      .subscribe(() => {
        this.showModal = false;
        this.personIdToDelete = '';
        this.onReloadMembers.emit();
      });
  }

  navigateToDetailsNewTab(id: string) {
    const url = this.router.serializeUrl(
      this.router.createUrlTree(['/employee/edit', id])
    );
    window.open(url, '_blank');
  }

  getAdditionalRows = (row: PersonModel) => {
    return this.getAdditionalPersons(row.id);
  };

  mapAdditionalRow = (p: any) => {
    return [
      `${p.lastName ?? ''}`.trim(),
      `${p.firstName ?? ''}`.trim(),
      ''  // Email column empty
    ];
  };
}